import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const MAX_PDF_POINTS = 14000;
const PX_TO_PT = 72 / 96;
const SCALE = 2;
const BLOCKS_PER_CANVAS = 10;

const withExpandedScroll = async (element, task) => {
  const saved = {
    height: element.style.height,
    maxHeight: element.style.maxHeight,
    overflow: element.style.overflow,
  };
  element.style.height = "auto";
  element.style.maxHeight = "none";
  element.style.overflow = "visible";
  try {
    return await task();
  } finally {
    element.style.height = saved.height;
    element.style.maxHeight = saved.maxHeight;
    element.style.overflow = saved.overflow;
  }
};

const captureElement = (element, ignoreBlockIds) =>
  html2canvas(element, {
    scale: SCALE,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
    ignoreElements: (el) =>
      typeof el.id === "string" &&
      el.id.startsWith("block_") &&
      ignoreBlockIds.has(el.id),
  });

const hideBlocks = (blocks) => {
  const saved = blocks.map((block) => ({
    block,
    display: block.style.display,
  }));
  blocks.forEach((block) => {
    block.style.display = "none";
  });
  return () => {
    saved.forEach(({ block, display }) => {
      block.style.display = display;
    });
  };
};

const canvasToPageSize = (canvas) => {
  const widthPt = (canvas.width / SCALE) * PX_TO_PT;
  const heightPt = (canvas.height / SCALE) * PX_TO_PT;
  const fit = heightPt > MAX_PDF_POINTS ? MAX_PDF_POINTS / heightPt : 1;
  return {
    widthPt: widthPt * fit,
    heightPt: heightPt * fit,
  };
};

const addCanvasPage = (pdf, canvas) => {
  const { widthPt, heightPt } = canvasToPageSize(canvas);
  const orientation = heightPt >= widthPt ? "portrait" : "landscape";
  const format = [widthPt, heightPt];

  if (!pdf) {
    pdf = new jsPDF({
      orientation,
      unit: "pt",
      format,
      compress: true,
    });
  } else {
    pdf.addPage(format, orientation);
  }

  pdf.addImage(
    canvas.toDataURL("image/jpeg", 0.92),
    "JPEG",
    0,
    0,
    widthPt,
    heightPt
  );
  canvas.width = 0;
  canvas.height = 0;
  return pdf;
};

const ExportConditionsPdf = async (element, fileName) => {
  if (!element) throw new Error("element is required");

  await withExpandedScroll(element, async () => {
    const blocks = Array.from(element.querySelectorAll('[id^="block_"]'));
    const chunks =
      blocks.length === 0
        ? [blocks]
        : Array.from(
            { length: Math.ceil(blocks.length / BLOCKS_PER_CANVAS) },
            (_, i) =>
              blocks.slice(i * BLOCKS_PER_CANVAS, (i + 1) * BLOCKS_PER_CANVAS)
          );

    let pdf = null;

    for (const chunk of chunks) {
      const hidden = blocks.filter((block) => !chunk.includes(block));
      const restore = hideBlocks(hidden);
      let canvas;
      try {
        canvas = await captureElement(
          element,
          new Set(hidden.map((block) => block.id))
        );
      } finally {
        restore();
      }
      pdf = addCanvasPage(pdf, canvas);
    }

    pdf.save(`${fileName}.pdf`);
  });
};

export default ExportConditionsPdf;
